import type { UserId } from "@gs/core/domain/User/User";
import { useSelector } from "react-redux";
import { getWhiteLabelName } from "store/whiteLabel/selectors";
import { Button } from "@gs/uiKit/general/Button";
import { sessionService } from "feature/session/SessionService";
import { Form } from "@gs/uiKit/input/Form";
import { TextWithOptions } from "./TextWithOptions";
import { useEffect, useState } from "react";
import { getError } from "services/utils";
import { Alert } from "@gs/uiKit/feedback/Alert";
import { useQualificationQuestions } from "domain/whitelabel/hook/useQualificationQuestions";
import type { QualificationQuestion } from "@gs/core/domain/Whitelabel/QualificationQuestion";
import { currentUserApi } from "feature/session/currentUserApi";
import { marketingAnalytics } from "feature/marketingAnalytics/marketingAnalytics";

type FormData = {
  [K in QualificationQuestion["key"]]: string
}

export const StepKnowYouBetter = (props: {userId: UserId}) => {
  const whiteLabelName = useSelector(getWhiteLabelName);
  const projectName = whiteLabelName || "GetSales.io";
  const [form] = Form.useForm();
  const [error, setError] = useState("");
  const qualificationQuestions = useQualificationQuestions();

  useEffect(() => {
    marketingAnalytics.fireEvent("sign_up_step_qualification");
  }, []);

  const submit = async (data: FormData) => {
    try {
      const { data: user } = await currentUserApi.qualify(data);
      if (user.qualification_status === "sql") {
        // @ts-ignore
        window._gs_sql = true;
      }
    } catch (theError) {
      setError(getError(theError));
    }
  };

  return (
    <Form
      form={form}
      onFinish={submit}
      validateTrigger="submit"
      style={{ height: "80%", display: "flex", flexDirection: "column" }}
    >
      <div className="gs-fs-28 gs-fw-500 gs-mb"> Help us personalize your experience</div>

      {error && (
        <Alert
          showIcon
          message={error}
          type="error"
          closable
          onClose={() => setError("")}
          className="gs-mb"
        />
      )}

      <div className="gs-fs-16 gs-mb-md">
        Tell us a bit about your business so we can set up
        {" "}
        {projectName}
        {" "}
        to fit your goals from day one.
      </div>
      <div className="gs-grow gs-h-0 gs-scroll gs-scroll-vertical-shadows">
        {qualificationQuestions?.map((theQuestion) => (
          <>
            <div className="gs-fs-16 gs-fw-500 gs-mb-xs">
              {theQuestion.question_text}
            </div>
            <Form.Item<FormData>
              name={theQuestion.key}
              rules={[{ required: true }]}
            >
              <TextWithOptions
                valuesPreset={theQuestion.options}
                withOtherOption={theQuestion.other_option}
                placeholder={theQuestion.key === "linkedin_url" ? "https://www.linkedin.com/in/kaliuzhnypeter/" : undefined}
              />
            </Form.Item>
          </>
        ))}
      </div>

      <Button
        type="primary"
        htmlType="submit"
        style={{ width: "100%" }}
        className="gs-mt-md gs-mb"
      >
        Continue
      </Button>

      <div>
        <span className="gs-c-secondary">
          Need to switch user?
        </span>
        {" "}
        <Button
          type="link"
          htmlType="button"
          size="small"
          onClick={() => sessionService.signOut()}
        >
          Logout
        </Button>
      </div>
    </Form>
  );
};
